import { UserPlus, X } from "lucide-react";
import { useMemo } from "react";
import { toast } from "sonner";

import { Combobox, type ComboOption } from "@/components/cinch/Combobox";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import {
  useSetStepAssignments,
  useStepAssignments,
  type StepAssignments,
} from "../api";
import { RECON_ROLE_LABEL, stepDisplayNo } from "../steps";

/** Admin tab: who works which reconciliation step.
 *
 *  Assigning a user grants them `reconciliation.read` implicitly and scopes
 *  their workbench to just their assigned steps (unless they also hold a
 *  role-based reconciliation permission, which keeps the full view). Removing
 *  a user's last assignment revokes the implicit read again. */
export function StepAssignmentsTab() {
  const { data, isLoading, error } = useStepAssignments();
  const update = useSetStepAssignments();

  if (isLoading && !data) {
    return (
      <Card className="space-y-3 p-5">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </Card>
    );
  }
  if (error) {
    return (
      <Card className="p-5 text-sm text-destructive">
        Couldn't load assignments: {(error as Error).message}
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        Assign users to the steps they work. Assigned users automatically get
        access to the reconciliation workbench, scoped to just their steps —
        role holders (RevOps, CS, …) keep the full view. Removing someone's
        last assignment removes their access again.
      </p>
      <Card className="divide-y divide-border overflow-hidden p-0">
        {(data?.steps ?? []).map((step) => (
          <StepAssignmentRow
            key={step.step_key}
            step={step}
            allUsers={data?.users ?? []}
            saving={update.isPending && update.variables?.stepKey === step.step_key}
            onChange={(userIds) =>
              update.mutate(
                { stepKey: step.step_key, userIds },
                {
                  onSuccess: () => toast.success(`${step.step_key} assignments saved`),
                  onError: (e) =>
                    toast.error("Couldn't save assignments", {
                      description: (e as Error).message,
                    }),
                },
              )
            }
          />
        ))}
      </Card>
    </div>
  );
}

function StepAssignmentRow({
  step,
  allUsers,
  saving,
  onChange,
}: {
  step: StepAssignments;
  allUsers: { user_id: string; name: string; email: string }[];
  saving: boolean;
  onChange: (userIds: string[]) => void;
}) {
  const assignedIds = useMemo(
    () => step.users.map((u) => u.user_id),
    [step.users],
  );
  const options = useMemo<ComboOption[]>(
    () =>
      allUsers
        .filter((u) => !assignedIds.includes(u.user_id))
        .map((u) => ({ id: u.user_id, label: u.name, subtitle: u.email })),
    [allUsers, assignedIds],
  );

  return (
    <div className="flex flex-wrap items-center gap-3 px-5 py-3">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-muted-foreground">
        {stepDisplayNo(step.order)}
      </div>
      <div className="min-w-0 flex-1 basis-56">
        <div className="truncate font-medium">{step.title}</div>
        <div className="text-xs text-muted-foreground">
          Owner role:{" "}
          {step.owner_roles
            .map((r) => RECON_ROLE_LABEL[r]?.short ?? r)
            .join(", ")}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {step.users.map((u) => (
          <span
            key={u.user_id}
            className="inline-flex items-center gap-1 rounded-full border border-brand/40 bg-brand-light px-2.5 py-0.5 text-xs font-medium text-brand"
            title={u.email}
          >
            {u.name}
            <button
              type="button"
              aria-label={`Remove ${u.name} from ${step.step_key}`}
              className="rounded-full p-0.5 hover:bg-brand/10"
              disabled={saving}
              onClick={() => onChange(assignedIds.filter((id) => id !== u.user_id))}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {step.users.length === 0 && (
          <span className="text-xs text-muted-foreground">No one assigned</span>
        )}
        <div className="w-56">
          <Combobox
            options={options}
            value={null}
            onChange={(id) => {
              if (id) onChange([...assignedIds, id]);
            }}
            placeholder={saving ? "Saving…" : "Assign user…"}
            searchPlaceholder="Search users…"
            emptyText="No matching users."
            disabled={saving}
          />
        </div>
        <UserPlus className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
      </div>
    </div>
  );
}
